import React, { useEffect, useState } from "react";
import { api, API as API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Send, Eye, History, CheckCircle2, AlertCircle, Loader2, Calendar } from "lucide-react";

/**
 * Trustee Weekly Digest — preview, send-now, history.
 *
 * The background scheduler sends automatically every Monday 07-10 UTC. This
 * panel lets the admin (a) preview the digest HTML in a sandboxed iframe,
 * (b) send the digest manually right now (e.g. ahead of a trustee meeting),
 * (c) review the audit log of past sends.
 */
export default function TrusteeDigestManager() {
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [overrideTo, setOverrideTo] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/digest/history");
      setHistory(r.data.items || []);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const sendNow = async () => {
    if (!confirm("Send the trustee digest now?")) return;
    setSending(true);
    try {
      const q = overrideTo ? `?to=${encodeURIComponent(overrideTo)}` : "";
      const { data } = await api.post(`/admin/digest/send-now${q}`);
      if (data.mocked) {
        toast.info("Digest queued in MOCK mode (Resend disabled).");
      } else if (data.delivered_count > 0) {
        toast.success(`Digest sent to ${data.delivered_count} recipient(s).`);
      } else {
        toast.error("Resend rejected the message. Check backend logs.");
      }
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="trustee-digest-manager">
      <section className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-saffron/15 border border-saffron/40 text-saffron">
            <Calendar size={18} />
          </span>
          <div className="flex-1">
            <h2 className="font-display text-2xl text-navy">Trustee weekly digest</h2>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-2xl">
              Every Monday morning, a one-page summary of the past week's
              activity is emailed to the trustees: new members, donations,
              inbound messages, RSVPs, governance-pack sends and the current
              CIO status. You can also preview it or send it on demand below.
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
              Override recipient (optional)
            </span>
            <input
              type="email"
              value={overrideTo}
              onChange={(e) => setOverrideTo(e.target.value)}
              placeholder="Leave blank to use configured trustee addresses"
              data-testid="digest-override-to"
              className="mt-2 w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
            />
          </label>
          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              data-testid="digest-preview-toggle"
              className="px-4 py-2.5 border border-navy/20 text-navy rounded-sm text-sm hover:bg-navy/5 inline-flex items-center gap-2"
            >
              <Eye size={14} /> {showPreview ? "Hide preview" : "Preview digest"}
            </button>
            <button
              type="button"
              onClick={sendNow}
              disabled={sending}
              data-testid="digest-send-now"
              className="px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm font-medium hover:bg-navy-light disabled:opacity-60 inline-flex items-center gap-2"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? "Sending…" : "Send digest now"}
            </button>
          </div>
        </div>

        {showPreview && (
          <div className="mt-6 border border-navy/10 rounded-sm overflow-hidden" data-testid="digest-preview-frame-wrap">
            <iframe
              src={`${API_BASE}/admin/digest/preview`}
              title="Trustee digest preview"
              className="w-full h-[700px] bg-white"
              data-testid="digest-preview-iframe"
            />
          </div>
        )}
      </section>

      <section className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <History size={18} className="text-saffron" />
          <h3 className="font-display text-xl text-navy">Sent history</h3>
          <button type="button" onClick={loadHistory} className="ml-auto text-xs text-slate-500 hover:text-navy">Refresh</button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No digests sent yet.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="digest-history-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-navy/10 text-xs uppercase tracking-widest text-slate-500">
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">Recipients</th>
                  <th className="py-2 pr-3">Members</th>
                  <th className="py-2 pr-3">Donations</th>
                  <th className="py-2 pr-3">Reason</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.digest_id} className="border-b border-navy/5 align-top">
                    <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                      {new Date(h.sent_at).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-3 text-navy">
                      {(h.recipients || []).join(", ")}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      +{h.stats?.new_members ?? 0} / {h.stats?.total_members ?? 0}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      £{(h.stats?.donation_total_gbp || 0).toFixed(0)} ({h.stats?.donations_count ?? 0})
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-500">{h.reason}</td>
                    <td className="py-2.5 pr-3">
                      {h.delivered_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                          <CheckCircle2 size={12} /> {h.delivered_count} delivered
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 text-xs">
                          <AlertCircle size={12} /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
